<template>
  <v-dialog v-model="viewFileModal" max-width="1000">
    <v-card v-if="value" class="rounded-lg">
      <v-toolbar dark>
        <v-btn icon dark @click="viewFileModal = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title>
          {{ filename | sanitizeFilename }}
        </v-toolbar-title>
        <v-spacer />
        <v-btn v-if="filePath" icon @click="downloadFile()">
          <v-icon>mdi-download</v-icon>
        </v-btn>
        <v-btn
          v-if="filePath && isEditableImage"
          icon
          @click="editImage = !editImage"
        >
          <v-icon :color="editImage ? 'primary' : 'warning'">mdi-pencil</v-icon>
        </v-btn>
      </v-toolbar>
      <v-container fluid v-if="filePath && !editImage">
        <v-img v-if="fileType === 'image'" :src="filePath" />
        <div v-if="fileType === 'html'" class="iframe-container">
          <iframe :src="`${filePath}`" />
        </div>
        <ModelViewer v-if="fileType === 'model'" :file-path="filePath" />
      </v-container>
      <ImageEditor
        v-if="filePath && editImage"
        v-model="filePath"
        @update="triggerUploadFile"
        @restore="triggerRestoreFile"
      />
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import ModelViewer from "@/shared/ModelViewer";
import ImageEditor from "@/shared/ImageEditor/ImageEditor.vue";
import options from "@/plugins/axios_options";

export default {
  name: "FileViewModal",

  props: ["value", "type", "caseUid"],

  components: {
    ModelViewer,
    ImageEditor,
  },

  data() {
    return {
      filePath: null,
      editImage: false,
    };
  },

  watch: {
    value(file) {
      if (!file) {
        return;
      }
      this.fetchFileURI();
    },
  },
  computed: {
    ...mapGetters(["kase", "collection"]),

    caseID() {
      if (this.kase.case_id) {
        return this.kase.case_id;
      }
      const kase = this.collection.cases.find(
        (kase) => kase.uid === this.caseUid
      );
      if (!kase) {
        return;
      }
      return kase.case_id;
    },

    fileType() {
      let fileExt = this.filename;
      fileExt = fileExt
        .toLowerCase()
        .split(".")
        .pop();
      switch (fileExt) {
        case "html":
          return "html";
        case "jpg":
        case "jpeg":
        case "png":
          return "image";
        case "ply":
        case "stl":
          return "model";
        default:
          return "download";
      }
    },

    filename() {
      return this.value.filename || this.value.file_name;
    },

    viewFileModal: {
      get() {
        if (this.value) {
          return true;
        }
        return false;
      },
      set(value) {
        this.$emit("input", value);
        this.filePath = null;
        this.editImage = false;
      },
    },

    isEditableImage() {
      return (
        this.fileType === "image" &&
        [
          "internal_file",
          "order_file",
          "approval_file",
          "outsource_received_file",
          "outsource_sent_file",
          "note_attachment",
          "message_attachment",
        ].includes(this.type)
      );
    },
  },
  methods: {
    ...mapActions(["startLoading", "stopLoading"]),

    async triggerUploadFile(file) {
      const editedFile = new File(
        [file],
        this.$options.filters["sanitizeFilename"](this.filename),
        { type: "image/png" }
      );

      if (this.type === "message_attachment") {
        const oftResponse = await this.handleFileUpload(editedFile);
        return await this.updateAttachment(oftResponse);
      }

      const formData = new FormData();
      formData.append("action", "blur_file");
      formData.append("type", this.type);
      formData.append("file_id", this.value.id || this.value.uid);
      formData.append("files", editedFile);

      this.$axios
        .post(`/api/v2/case/${this.caseID}/`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then(() => {
          this.viewFileModal = false;
        });
    },

    async triggerRestoreFile() {
      if (this.type === "message_attachment") {
        return this.restoreAttachment();
      }

      this.$axios
        .post(`/api/v2/case/${this.caseID}/`, {
          action: "restore_file",
          type: this.type,
          file_id: this.value.id,
        })
        .then(() => {
          this.viewFileModal = false;
        });
    },

    async fetchFileURI() {
      if (this.type === "message_attachment") {
        return this.fetchAttachmentURI();
      }

      this.startLoading();
      this.$axios
        .post(`/api/v2/case/${this.caseID}/`, {
          action: "file_link",
          type: this.type,
          file_id: this.value.id,
        })
        .then((response) => {
          this.filePath = response.data;
          if (this.fileType === "download") {
            this.downloadFile();
          }
          this.stopLoading();
        });
    },

    async fetchAttachmentURI() {
      this.startLoading();
      this.$axios
        .get(`/api/v2/messages/attachments/${this.value.uid}/`)
        .then((response) => {
          this.filePath = response.data.url;
          if (this.fileType === "download") {
            this.downloadFile();
          }
          this.stopLoading();
        });
    },

    async updateAttachment(oftResponse) {
      this.startLoading();
      this.$axios
        .post(`/api/v2/messages/attachments/${this.value.uid}/`, {
          action: "blur_attachment",
          ref_id: this.collection.uid,
          filename: oftResponse.file_name,
        })
        .then(() => {
          this.$emit("refresh");
          this.viewFileModal = false;
          this.stopLoading();
        });
    },

    async restoreAttachment() {
      this.startLoading();
      this.$axios
        .post(`/api/v2/messages/attachments/${this.value.uid}/`, {
          action: "restore_attachment",
          ref_id: this.collection.uid,
        })
        .then(() => {
          this.$emit("refresh");
          this.viewFileModal = false;
          this.stopLoading();
        });
    },

    downloadFile() {
      window.open(this.filePath, "_blank");
      this.$emit("input", false);
    },

    async handleFileUpload(file) {
      const oftResponse = await this.oftAddFile(file);
      await this.uploadToGoogleStorage(
        file,
        oftResponse.data[0].oneTimeUploadLink
      );

      return {
        file_name: oftResponse.data[0].fileName,
        oft_file_uid: oftResponse.data[0].uid,
      };
    },

    async oftAddFile(file) {
      try {
        const country = this.collection.shipping_client.country;
        const response = await this.$axios.post(
          `${options.oftURL}/upload/${country}`,
          [
            {
              fileName: file.name,
            },
          ]
        );

        return response;
      } catch (error) {
        console.error(error);
        throw error;
      }
    },

    async uploadToGoogleStorage(file, uploadLink) {
      try {
        const response = await fetch(uploadLink, {
          method: "PUT",
          body: file,
        });

        return response;
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
  },
};
</script>

<style scoped>
.iframe-container {
  overflow: hidden;
  padding-top: 56.25%;
  position: relative;
}

.iframe-container iframe {
  border: 0;
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
}
</style>
